import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  listSignupRequests,
  approveSignupRequest,
  rejectSignupRequest,
} from '../services/adminService';
import { CHAINS, getPopularChains } from '../config/chains';
import { getApplicationSelectOptions } from '../config/applications';
import { assetUrl } from '../utils/assets';
import { toUserFriendlyError } from '../utils/friendlyError';
import SelectField from './SelectField';
import ChangePasswordModal from './ChangePasswordModal';
import './Dashboard.css';
import './AdminRequests.css';

const ALL_VALUE = '-';
const APPLICATION_OPTIONS = getApplicationSelectOptions(ALL_VALUE);

const emptyRow = () => ({
  chain: ALL_VALUE,
  token: ALL_VALUE,
  application: ALL_VALUE,
  userAddress: '',
});

const buildChainOptions = () => {
  const popular = getPopularChains();
  const seen = new Set(popular.map((c) => c.id));
  const rest = Object.values(CHAINS)
    .filter((c) => !seen.has(c.id))
    .sort((a, b) => a.name.localeCompare(b.name));
  return [
    { value: ALL_VALUE, label: 'All chains' },
    ...[...popular, ...rest].map((chain) => ({
      value: chain.id,
      label: chain.name,
    })),
  ];
};

const tokensForChain = (chainId) => {
  const symbols = new Set();
  if (chainId && chainId !== ALL_VALUE && CHAINS[chainId]) {
    Object.values(CHAINS[chainId].tokens || {}).forEach((t) => {
      if (t?.symbol) symbols.add(t.symbol);
    });
  } else {
    Object.values(CHAINS).forEach((chain) => {
      Object.values(chain.tokens || {}).forEach((t) => {
        if (t?.symbol) symbols.add(t.symbol);
      });
    });
  }
  return [
    { value: ALL_VALUE, label: 'All tokens' },
    ...Array.from(symbols)
      .sort((a, b) => a.localeCompare(b))
      .map((symbol) => ({ value: symbol, label: symbol })),
  ];
};

const CHAIN_OPTIONS = buildChainOptions();

const formatSubmittedAt = (value) => {
  if (!value) return '';
  try {
    const date = value.toDate ? value.toDate() : new Date(value);
    if (Number.isNaN(date.getTime())) return String(value);
    return date.toLocaleString();
  } catch (_) {
    return String(value);
  }
};

const AccessRuleRow = ({ requestId, index, row, onChange, onRemove }) => {
  const tokenOptions = tokensForChain(row.chain);
  const ruleNum = index + 1;
  const chainId = `${requestId}-chain-${index}`;
  const tokenId = `${requestId}-token-${index}`;
  const appId = `${requestId}-app-${index}`;
  const addressId = `${requestId}-address-${index}`;

  return (
    <div className="admin-access-row">
      <div className="admin-access-field">
        <label className="visually-hidden" htmlFor={chainId}>
          Chain for rule {ruleNum}
        </label>
        <SelectField
          id={chainId}
          value={row.chain}
          onChange={(value) => onChange(requestId, index, 'chain', value)}
          options={CHAIN_OPTIONS}
          placeholder="Chain"
        />
      </div>
      <div className="admin-access-field">
        <label className="visually-hidden" htmlFor={tokenId}>
          Token for rule {ruleNum}
        </label>
        <SelectField
          id={tokenId}
          value={row.token}
          onChange={(value) => onChange(requestId, index, 'token', value)}
          options={tokenOptions}
          placeholder="Token"
        />
      </div>
      <div className="admin-access-field">
        <label className="visually-hidden" htmlFor={appId}>
          Application for rule {ruleNum}
        </label>
        <SelectField
          id={appId}
          value={row.application}
          onChange={(value) => onChange(requestId, index, 'application', value)}
          options={APPLICATION_OPTIONS}
          placeholder="Application"
        />
      </div>
      <div className="admin-access-field">
        <label className="visually-hidden" htmlFor={addressId}>
          User address for rule {ruleNum}
        </label>
        <input
          id={addressId}
          className="admin-input small"
          value={row.userAddress}
          placeholder="All addresses"
          onChange={(e) => onChange(requestId, index, 'userAddress', e.target.value)}
          spellCheck={false}
          autoComplete="off"
        />
      </div>
      <button
        type="button"
        className="admin-remove-row"
        onClick={() => onRemove(requestId, index)}
        aria-label={`Remove access rule ${ruleNum}`}
      >
        ×
      </button>
    </div>
  );
};

const AdminRequests = ({ user, auditorData, onLogout, onKeyMaterialUpdated }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const highlightId = new URLSearchParams(location.search).get('id');
  const reloadTimerRef = useRef(null);
  const highlightRef = useRef(null);

  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editors, setEditors] = useState({});
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);

  useEffect(() => {
    document.title = 'Signup requests · Compliance Dashboard';
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const rows = await listSignupRequests('pending');
      setRequests(rows);
      setEditors((prev) => {
        const next = { ...prev };
        rows.forEach((r) => {
          if (!next[r.id]) {
            next[r.id] = { name: r.name || '', rows: [], busy: false, busyAction: null, msg: null, msgType: null, reason: '' };
          }
        });
        return next;
      });
    } catch (err) {
      setError(toUserFriendlyError(err, 'Failed to load signup requests. Please try again.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    return () => {
      if (reloadTimerRef.current) window.clearTimeout(reloadTimerRef.current);
    };
  }, [load]);

  useEffect(() => {
    if (!highlightId || loading) return;
    const node = highlightRef.current;
    if (node) {
      node.scrollIntoView({ behavior: 'smooth', block: 'center' });
      node.focus();
    }
  }, [highlightId, loading, requests]);

  const updateEditor = (id, patch) =>
    setEditors((prev) => ({ ...prev, [id]: { ...prev[id], ...patch } }));

  const updateRow = (id, idx, field, value) =>
    setEditors((prev) => {
      const rows = [...(prev[id].rows || [])];
      const next = { ...rows[idx], [field]: value };
      if (field === 'chain') {
        const allowed = new Set(tokensForChain(value).map((o) => o.value));
        if (!allowed.has(next.token)) next.token = ALL_VALUE;
      }
      rows[idx] = next;
      return { ...prev, [id]: { ...prev[id], rows } };
    });

  const addRow = (id) =>
    setEditors((prev) => ({
      ...prev,
      [id]: { ...prev[id], rows: [...(prev[id].rows || []), emptyRow()] },
    }));

  const removeRow = (id, idx) =>
    setEditors((prev) => {
      const rows = [...(prev[id].rows || [])];
      rows.splice(idx, 1);
      return { ...prev, [id]: { ...prev[id], rows } };
    });

  const scheduleReload = () => {
    if (reloadTimerRef.current) window.clearTimeout(reloadTimerRef.current);
    reloadTimerRef.current = window.setTimeout(load, 900);
  };

  const handleApprove = async (id) => {
    const editor = editors[id];
    updateEditor(id, { busy: true, busyAction: 'approve', msg: null, msgType: null });
    try {
      const accessControls = (editor.rows || []).map((r) => [
        (r.chain || '-').trim() || '-',
        (r.token || '-').trim() || '-',
        (r.application || '-').trim() || '-',
        (r.userAddress || '-').trim() || '-',
      ]);
      const result = await approveSignupRequest(id, accessControls, editor.name?.trim() || null);
      let msg;
      if (result.alreadyProcessed) {
        msg = 'Already processed.';
      } else if (result.emailSent) {
        msg = `Approved. Invite email sent to ${result.email}.`;
      } else {
        msg = `Approved, but invite email failed (${result.emailError || 'unknown error'}). Share this link manually: ${result.inviteUrl || '(none)'}`;
      }
      const bf = result.grantsBackfill;
      if (bf && !bf.error && typeof bf.written === 'number') {
        msg += ` Historical grants: ${bf.written} written (${bf.matched} matched).`;
      } else if (bf?.error) {
        msg += ` Grant backfill failed: ${bf.error}`;
      } else if (!accessControls.length) {
        msg += ' No access scopes. Grant backfill skipped.';
      }
      updateEditor(id, { busy: false, busyAction: null, msg, msgType: 'success' });
      scheduleReload();
    } catch (err) {
      updateEditor(id, {
        busy: false,
        busyAction: null,
        msg: toUserFriendlyError(err, 'Failed to approve this request. Please try again.'),
        msgType: 'error',
      });
    }
  };

  const handleReject = async (id) => {
    const req = requests.find((r) => r.id === id);
    const label = req?.email || req?.name || 'this request';
    if (!window.confirm(`Reject signup request for ${label}?`)) return;

    const editor = editors[id];
    updateEditor(id, { busy: true, busyAction: 'reject', msg: null, msgType: null });
    try {
      await rejectSignupRequest(id, editor?.reason?.trim() || null, true);
      updateEditor(id, { busy: false, busyAction: null, msg: 'Rejected.', msgType: 'success' });
      scheduleReload();
    } catch (err) {
      updateEditor(id, {
        busy: false,
        busyAction: null,
        msg: toUserFriendlyError(err, 'Failed to reject this request. Please try again.'),
        msgType: 'error',
      });
    }
  };

  return (
    <div className="admin-requests">
      <header className="dashboard-header">
        <div className="header-content">
          <div className="header-title">
            <img src={assetUrl('logo.png')} alt="Compliance Dashboard logo" className="dashboard-logo" />
            <div className="header-title-text">
              <h1>Signup Requests</h1>
              <span className="header-subtitle">Access review queue</span>
            </div>
          </div>
          <div className="header-actions">
            <button type="button" onClick={() => navigate('/')} className="admin-secondary-button">
              Dashboard
            </button>
            <div className="header-user">
              <span className="header-user-avatar" aria-hidden="true">
                {(user?.email || '?').charAt(0)}
              </span>
              <span className="user-email">{user?.email}</span>
            </div>
            <button
              type="button"
              onClick={() => setChangePasswordOpen(true)}
              className="header-nav-button"
            >
              Change Password
            </button>
            <button type="button" onClick={onLogout} className="logout-button">
              Log Out
            </button>
          </div>
        </div>
      </header>

      <main className="admin-main">
        {loading && (
          <div className="admin-loading" role="status" aria-live="polite" aria-busy="true">
            <div className="loading-spinner" aria-hidden="true" />
            <p>Loading requests…</p>
          </div>
        )}
        {error && (
          <div className="error-message ui-alert ui-alert--error" role="alert">
            <span>{error}</span>
            <button type="button" className="ui-btn ui-btn--secondary" onClick={load}>
              Retry
            </button>
          </div>
        )}
        {!loading && !error && requests.length === 0 && (
          <div className="admin-empty">
            <p className="admin-note">No pending signup requests.</p>
            <button type="button" className="ui-btn ui-btn--secondary" onClick={load}>
              Refresh
            </button>
          </div>
        )}

        <div className="admin-request-list">
          {requests.map((req) => {
            const editor = editors[req.id] || { rows: [], name: '' };
            const isHighlighted = highlightId && highlightId === req.id;
            const submittedLabel = formatSubmittedAt(req.createdAt);
            return (
              <div
                key={req.id}
                ref={isHighlighted ? highlightRef : null}
                tabIndex={isHighlighted ? -1 : undefined}
                className={`admin-request-card${isHighlighted ? ' highlighted' : ''}`}
              >
                <div className="admin-request-fields">
                  {[
                    ['Name', req.name],
                    ['Email', req.email, req.email ? `mailto:${req.email}` : null],
                    ['Company', req.company],
                    ['Role', req.role],
                    ['Website', req.website, req.website],
                    ['Details', req.extraDetails],
                    ['Submitted', submittedLabel, null, req.createdAt],
                  ]
                    .filter(([, v]) => v)
                    .map(([k, v, href, title]) => (
                      <div className="admin-field-row" key={k}>
                        <span className="admin-field-label">{k}</span>
                        <span className="admin-field-value" title={title || undefined}>
                          {href ? (
                            <a
                              href={href.startsWith('mailto:') || href.startsWith('http') ? href : `https://${href}`}
                              target={href.startsWith('mailto:') ? undefined : '_blank'}
                              rel={href.startsWith('mailto:') ? undefined : 'noopener noreferrer'}
                            >
                              {v}
                            </a>
                          ) : (
                            v
                          )}
                        </span>
                      </div>
                    ))}
                </div>

                <div className="admin-access-editor">
                  <label className="admin-input-label" htmlFor={`${req.id}-name`}>
                    Display name (optional)
                  </label>
                  <input
                    id={`${req.id}-name`}
                    type="text"
                    className="admin-input"
                    value={editor.name}
                    onChange={(e) => updateEditor(req.id, { name: e.target.value })}
                    placeholder="Auditor name"
                  />

                  <div className="admin-access-header">
                    <span>Access controls</span>
                    <button type="button" className="admin-add-row" onClick={() => addRow(req.id)}>
                      + Add rule
                    </button>
                  </div>
                  <p className="admin-hint">
                    Leave empty for no access (safe default). Pick chain, token, and application from
                    the menus. Choose “All …” to grant broadly. Enter a wallet address to limit to
                    one account, or leave it blank for all addresses.
                  </p>

                  {(editor.rows || []).length === 0 && (
                    <p className="admin-empty-access">
                      No access rules. Auditor will see nothing until scopes are added.
                    </p>
                  )}

                  {(editor.rows || []).length > 0 && (
                    <div className="admin-access-row admin-access-row--labels" aria-hidden="true">
                      <span>Chain</span>
                      <span>Token</span>
                      <span>Application</span>
                      <span>User address</span>
                      <span />
                    </div>
                  )}

                  {(editor.rows || []).map((row, idx) => (
                    <AccessRuleRow
                      key={idx}
                      requestId={req.id}
                      index={idx}
                      row={row}
                      onChange={updateRow}
                      onRemove={removeRow}
                    />
                  ))}

                  <div className="admin-reject-reason">
                    <label className="visually-hidden" htmlFor={`${req.id}-reason`}>
                      Rejection reason
                    </label>
                    <input
                      id={`${req.id}-reason`}
                      type="text"
                      className="admin-input"
                      value={editor.reason || ''}
                      onChange={(e) => updateEditor(req.id, { reason: e.target.value })}
                      placeholder="Rejection reason (optional, emailed to requester)"
                    />
                  </div>

                  {editor.msg && (
                    <div
                      className={`admin-msg admin-msg--${editor.msgType || 'info'}`}
                      role={editor.msgType === 'error' ? 'alert' : 'status'}
                    >
                      {editor.msg}
                    </div>
                  )}

                  <div className="admin-actions">
                    <button
                      type="button"
                      className="admin-approve-button"
                      disabled={editor.busy}
                      onClick={() => handleApprove(req.id)}
                    >
                      {editor.busyAction === 'approve' ? 'Approving…' : 'Approve & send invite'}
                    </button>
                    <button
                      type="button"
                      className="admin-reject-button"
                      disabled={editor.busy}
                      onClick={() => handleReject(req.id)}
                    >
                      {editor.busyAction === 'reject' ? 'Rejecting…' : 'Reject'}
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </main>

      {changePasswordOpen && auditorData?.id && (
        <ChangePasswordModal
          user={user}
          auditorDocId={auditorData.id}
          keyMaterial={{
            publicKey: auditorData.publicKey,
            encPrivKey: auditorData.encPrivKey,
            salt: auditorData.salt,
            kdfParams: auditorData.kdfParams,
            iv: auditorData.iv,
            keyVersion: auditorData.keyVersion,
          }}
          onClose={() => setChangePasswordOpen(false)}
          onSuccess={(updated) => {
            onKeyMaterialUpdated?.(updated);
          }}
        />
      )}
    </div>
  );
};

export default AdminRequests;
